import React, { useMemo } from 'react';

import { LocalizeCurrency } from '@change-corgi/core/react/i18n';
import { buttonResetStyles } from '@change-corgi/design-system-legacy/reset';
import { Text } from '@change-corgi/design-system-legacy/typography';

// Exporting in and importing from 'src/app/shared/components/membershipAsk' caused circular dependency issues that broke MembershipAskModal.integration.test.tsx
// eslint-disable-next-line @change/sibling-module-imports
import { useSunriseHoldoutExperiment } from 'src/app/shared/components/membershipAsk/sunriseHoldoutExperiment/hooks/useSunriseHoldoutExperiment';

type Props = {
	amount: number;
	currency: string;
	isSelected: boolean;
	onClick: () => void;
	dataQaAttribute?: string;
	isSunriseEnabled: boolean;
	height?: `${number}px`;
};

export function AmountButton({
	amount,
	isSelected,
	onClick,
	currency,
	dataQaAttribute,
	height,
	...props
}: Props): React.JSX.Element {
	const { enabled, isSunriseEnabled } = useSunriseHoldoutExperiment({ isSunriseEnabled: props.isSunriseEnabled });

	const colorStyles = useMemo(() => {
		if (isSelected)
			return {
				color: 'typography-primary',
				backgroundColor: 'rgba(21, 125, 185, 0.08)',
				borderColor: 'secondary-blue',
				...(isSunriseEnabled && {
					backgroundColor: 'sunrise.semantic.fill.accent.primary.weaker',
					borderColor: 'sunrise.semantic.fill.accent.primary.strong',
				}),
			};
		return {
			color: 'typography-primary',
			backgroundColor: 'primary-white',
			borderColor: 'neutral-grey100',
			...(isSunriseEnabled && {
				borderColor: 'sunrise.semantic.fill.weak',
			}),
		};
	}, [isSelected, isSunriseEnabled]);

	return (
		<button
			type="button"
			data-amount={amount}
			data-testid="payment-form-amount-button"
			data-qa={dataQaAttribute || 'contribution-amount-button'}
			onClick={onClick}
			aria-pressed={isSelected}
			aria-label={`${amount} ${currency}`}
			sx={{
				...buttonResetStyles,
				borderStyle: 'solid',
				borderWidth: isSelected ? '2px' : '1px',
				borderRadius: 'standard',
				width: '100%',
				height: height ?? '48px',
				fontWeight: 'bold',
				// eslint-disable-next-line @typescript-eslint/naming-convention
				':focus-within': {
					outlineWidth: '1px',
					outlineColor: 'secondary-blue',
					outlineStyle: 'solid',
				},
				...colorStyles,
			}}
		>
			{enabled ? (
				<Text size="default" fontWeight="bold">
					<LocalizeCurrency value={amount} currency={currency} />
				</Text>
			) : (
				<Text size="small" fontWeight="semibold">
					<LocalizeCurrency value={amount} currency={currency} />
				</Text>
			)}
		</button>
	);
}
